import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Modal } from "../ui/Modal";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { RoleSelector } from "../ui/RoleSelector";
import { useRoles } from "../../hooks/useRoles";
import { useDatasources } from "../../hooks/useDatasources";
import { useProvisionOrganisation } from "../../hooks/useProvisionOrg";
import { adminService } from "../../services/admin.service";
import { ProvisionOrgResponse, Role, Datasource } from "../../domain/types/admin.types";

interface OrgWizardProps {
  open: boolean;
  onClose: () => void;
  onCreated: (res: ProvisionOrgResponse) => void;
}

const step1Schema = z.object({
  organisationName: z.string().min(2, "Organisation name must be at least 2 characters"),
});

const step2Schema = z.object({
  adminEmail: z.string().min(1, "Email is required").email("Invalid email address"),
  adminFirstName: z.string().min(1, "First name is required"),
  adminLastName: z.string().min(1, "Last name is required"),
});

type Step1Form = z.infer<typeof step1Schema>;
type Step2Form = z.infer<typeof step2Schema>;

const SYSTEM_ROLES = new Set(["ROLE_ADMIN", "ROLE_USER"]);

const STEP_TITLES = ["Organisation", "Admin User", "Roles", "Datasource"];
const TOTAL_STEPS = 4;

export const OrgWizard: React.FC<OrgWizardProps> = ({ open, onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [step1Data, setStep1Data] = useState<Step1Form>({ organisationName: "" });
  const [step2Data, setStep2Data] = useState<Step2Form>({
    adminEmail: "",
    adminFirstName: "",
    adminLastName: "",
  });
  const [selectedRoles, setSelectedRoles] = useState<string[]>(["ROLE_ORG_ADMIN"]);
  const [rolesError, setRolesError] = useState("");
  const [selectedDatasourceId, setSelectedDatasourceId] = useState("");
  const [dsError, setDsError] = useState("");
  const [linkError, setLinkError] = useState("");
  const [provisionedOrgId, setProvisionedOrgId] = useState("");

  const rolesQuery = useRoles();
  const allRoles: Role[] = rolesQuery.data ?? [];
  const dsQuery = useDatasources();
  const datasources: Datasource[] = dsQuery.data ?? [];
  const provision = useProvisionOrganisation();

  const assignableRoles = allRoles.filter((r) => !SYSTEM_ROLES.has(r.name));

  const form1 = useForm<Step1Form>({
    resolver: zodResolver(step1Schema),
    defaultValues: step1Data,
    mode: "onChange",
  });

  const form2 = useForm<Step2Form>({
    resolver: zodResolver(step2Schema),
    defaultValues: step2Data,
    mode: "onChange",
  });

  const handleClose = () => {
    setStep(1);
    setStep1Data({ organisationName: "" });
    setStep2Data({ adminEmail: "", adminFirstName: "", adminLastName: "" });
    setSelectedRoles(["ROLE_ORG_ADMIN"]);
    setRolesError("");
    setSelectedDatasourceId("");
    setDsError("");
    setLinkError("");
    setProvisionedOrgId("");
    form1.reset({ organisationName: "" });
    form2.reset({ adminEmail: "", adminFirstName: "", adminLastName: "" });
    provision.reset();
    onClose();
  };

  const handleStep1 = form1.handleSubmit((data) => {
    setStep1Data(data);
    setStep(2);
  });

  const handleStep2 = form2.handleSubmit((data) => {
    setStep2Data(data);
    setStep(3);
  });

  const handleStep3 = () => {
    if (selectedRoles.length === 0) {
      setRolesError("Select at least one role");
      return;
    }
    setRolesError("");
    setStep(4);
  };

  const handleSubmit = async () => {
    setDsError("");
    setLinkError("");

    if (!selectedDatasourceId) {
      setDsError("Select a datasource");
      return;
    }

    let res: ProvisionOrgResponse;
    try {
      res = await provision.mutateAsync({
        organisationName: step1Data.organisationName,
        adminEmail: step2Data.adminEmail,
        adminFirstName: step2Data.adminFirstName,
        adminLastName: step2Data.adminLastName,
        roles: selectedRoles,
      });
    } catch {
      return; // error shown via provision.error
    }

    // Link datasource — if this fails, org is created but not linked
    try {
      await adminService.linkDatasourceToOrg(selectedDatasourceId, res.organisationId);
    } catch {
      setProvisionedOrgId(res.organisationId);
      setLinkError(
        `Organisation created (ID: ${res.organisationId}) but datasource linking failed. Please link it manually from the Org detail page.`
      );
      return;
    }

    onCreated(res);
    handleClose();
  };

  const modalTitle = `Create Organisation — Step ${step} of ${TOTAL_STEPS}: ${STEP_TITLES[step - 1]}`;

  return (
    <Modal open={open} onClose={handleClose} title={modalTitle} className="max-w-md">
      {/* Step indicators */}
      <div className="flex items-center gap-2 px-6 pt-4 pb-2">
        {[1, 2, 3, 4].map((s) => (
          <React.Fragment key={s}>
            <div
              className={`flex items-center justify-center h-6 w-6 rounded-full text-xs font-semibold flex-shrink-0 ${
                s <= step ? "bg-primary-blue text-white" : "bg-default-99 text-gray-400"
              }`}
            >
              {s}
            </div>
            {s < TOTAL_STEPS && (
              <div className={`flex-1 h-0.5 ${s < step ? "bg-primary-blue" : "bg-default-99"}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Organisation Name */}
      {step === 1 && (
        <form onSubmit={handleStep1} className="px-6 py-4 flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Organisation Name</label>
            <Input
              {...form1.register("organisationName")}
              placeholder="e.g. Acme Health Clinic"
              error={!!form1.formState.errors.organisationName}
              className="w-full"
            />
            {form1.formState.errors.organisationName && (
              <p className="text-xs text-red-500">
                {form1.formState.errors.organisationName.message}
              </p>
            )}
          </div>
          <div className="flex justify-end pt-2">
            <Button type="submit">Next →</Button>
          </div>
        </form>
      )}

      {/* Step 2: Admin User */}
      {step === 2 && (
        <form onSubmit={handleStep2} className="px-6 py-4 flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Admin Email</label>
            <Input
              {...form2.register("adminEmail")}
              placeholder="admin@example.com"
              error={!!form2.formState.errors.adminEmail}
              className="w-full"
            />
            {form2.formState.errors.adminEmail && (
              <p className="text-xs text-red-500">{form2.formState.errors.adminEmail.message}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-700">First Name</label>
              <Input
                {...form2.register("adminFirstName")}
                placeholder="Jane"
                error={!!form2.formState.errors.adminFirstName}
                className="w-full"
              />
              {form2.formState.errors.adminFirstName && (
                <p className="text-xs text-red-500">
                  {form2.formState.errors.adminFirstName.message}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-700">Last Name</label>
              <Input
                {...form2.register("adminLastName")}
                placeholder="Smith"
                error={!!form2.formState.errors.adminLastName}
                className="w-full"
              />
              {form2.formState.errors.adminLastName && (
                <p className="text-xs text-red-500">
                  {form2.formState.errors.adminLastName.message}
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-between pt-2">
            <Button type="button" variant="secondary" onClick={() => setStep(1)}>
              ← Back
            </Button>
            <Button type="submit">Next →</Button>
          </div>
        </form>
      )}

      {/* Step 3: Roles */}
      {step === 3 && (
        <div className="px-6 py-4 flex flex-col gap-4">
          <p className="text-xs text-gray-500">
            Select roles for{" "}
            <strong>
              {step2Data.adminFirstName} {step2Data.adminLastName}
            </strong>
            .
          </p>

          {rolesQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Spinner className="h-4 w-4" /> Loading roles…
            </div>
          ) : assignableRoles.length === 0 ? (
            <p className="text-sm text-gray-400">No assignable roles found.</p>
          ) : (
            <RoleSelector
              roles={assignableRoles}
              selected={selectedRoles}
              onChange={setSelectedRoles}
              maxHeight="max-h-52"
            />
          )}

          {rolesError && <p className="text-xs text-red-500">{rolesError}</p>}

          <div className="flex justify-between pt-2">
            <Button type="button" variant="secondary" onClick={() => setStep(2)}>
              ← Back
            </Button>
            <Button type="button" onClick={handleStep3}>
              Next →
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Datasource */}
      {step === 4 && (
        <div className="px-6 py-4 flex flex-col gap-4">
          <p className="text-xs text-gray-500">
            Select which database this organisation's data will be read from.
          </p>

          {dsQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Spinner className="h-4 w-4" /> Loading datasources…
            </div>
          ) : datasources.length === 0 ? (
            <p className="text-sm text-red-500">
              No datasources configured. Create one first in the backend.
            </p>
          ) : (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-700">Datasource</label>
              <select
                value={selectedDatasourceId}
                onChange={(e) => {
                  setSelectedDatasourceId(e.target.value);
                  setDsError("");
                }}
                className={`h-10 w-full rounded border bg-white px-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-blue ${
                  dsError ? "border-red-500" : "border-border-gray"
                }`}
              >
                <option value="">— Select datasource —</option>
                {datasources.map((ds: Datasource) => (
                  <option key={ds.id} value={ds.id}>
                    {ds.name} — {ds.host}:{ds.port}/{ds.dbName}
                  </option>
                ))}
              </select>
              {dsError && <p className="text-xs text-red-500">{dsError}</p>}
            </div>
          )}

          {provision.isError && (
            <p className="text-xs text-red-500">
              {(provision.error as any)?.data?.message ?? "Failed to create organisation."}
            </p>
          )}

          {linkError && (
            <div className="rounded border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
              <p className="font-medium mb-1">Datasource link failed</p>
              <p>{linkError}</p>
              {provisionedOrgId && (
                <button
                  onClick={() => {
                    onCreated({ organisationId: provisionedOrgId, userId: "", message: "" });
                    handleClose();
                  }}
                  className="mt-2 text-link-blue underline"
                >
                  View organisation →
                </button>
              )}
            </div>
          )}

          <div className="flex justify-between pt-2">
            <Button type="button" variant="secondary" onClick={() => setStep(3)} disabled={provision.isPending}>
              ← Back
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={provision.isPending || datasources.length === 0}
            >
              {provision.isPending ? (
                <span className="flex items-center gap-2">
                  <Spinner className="h-4 w-4 border-white border-r-transparent" />
                  Creating…
                </span>
              ) : (
                "Create Organisation"
              )}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
